import { useState } from "react";
import { useAuth } from "react-oidc-context";
import workspaceService, { ICreateWorkspace } from "api/workspaceService";
import { getUserId } from "utils/getUserId";
import { getKeys } from "utils/getKeys";
import {
  buildCompanyKeyPayload,
  ICreateCompanyKeyPayload,
} from "utils/buildCompanyKeyPayload";

// Kształt ładunku i generowanie klucza firmy mieszkają w utils/buildCompanyKeyPayload —
// ta sama krypto obsługuje bootstrap klucza dla firmy JUŻ istniejącej
// (components/CompanyKeyBootstrapGate). Dwie kopie rozjechałyby się przy pierwszej zmianie.
export type { ICreateCompanyKeyPayload };

/**
 * Logika tworzenia zespołu (workspace = FIRMA; ten sam wiersz Workspaces, patrz
 * docs/model-kluczy-firmowych.md, sekcja 1). Wydzielona z komponentu CreateWorkspace, żeby
 * ta sama operacja mogła być użyta w kreatorze pierwszej aktywacji (FirstActivationWizard)
 * bez duplikacji operacji kryptograficznych. Zwraca Id utworzonego zespołu.
 *
 * Co zmienił model kluczy firmowych: nie ma już profili, więc nie ma czego udostępniać
 * administracji (`ProfileSharedPrivateKey` — dawne „przejęcie profilu"; BE trzyma te pola
 * już tylko jako martwy kształt kontraktu). Zamiast tego FE generuje PARĘ KLUCZY FIRMY
 * (secp256k1, bez hasła) i wysyła ją w polu `CompanyKey`, z której BE zakłada generację 1
 * i grant dla założyciela.
 *
 * NIENARUSZALNE (I4): klucz prywatny firmy nie opuszcza przeglądarki odszyfrowany —
 * na serwer idzie WYŁĄCZNIE owinięty kluczem pośrednim założyciela.
 */
export const useCreateTeam = () => {
  const auth = useAuth();
  const { mutateAsync: createWorkspace } =
    workspaceService.useCreateWorkspace();
  const [loading, setLoading] = useState(false);

  const createTeam = async (name: string): Promise<string> => {
    setLoading(true);

    try {
      const userId = getUserId(auth.user!);
      const currentUserKeys = await getKeys(userId);

      if (!currentUserKeys?.intermidatePrivateKey) {
        throw new Error("Brak kluczy użytkownika");
      }

      const companyKey = await buildCompanyKeyPayload(userId, currentUserKeys);

      // PublicKey/PrivateKey (stary kształt żądania) niosą TĘ SAMĄ parę co CompanyKey —
      // kolumny Workspaces istnieją nadal, a BE nie przestał ich zapisywać.
      const payload: ICreateWorkspace & {
        CompanyKey: ICreateCompanyKeyPayload;
      } = {
        Name: name,
        PublicKey: companyKey.PublicKey,
        PrivateKey: companyKey.Grants[0].PrivateKey,
        CompanyKey: companyKey,
      };

      const response = await createWorkspace(payload);

      return response.data.Id;
    } finally {
      setLoading(false);
    }
  };

  return { createTeam, loading };
};

export default useCreateTeam;
